<template>
  <div class="stripe-connect-section">
    <hr class="my-4" />
    <div class="d-flex align-items-center mb-2">
      <strong>Stripe Payments</strong>
    </div>
    <p class="text-muted mb-3">{{ helperText }}</p>

    <div v-if="loading" class="my-3 text-muted">
      <em>Verifying with Stripe…</em>
    </div>

    <div v-else>
      <div class="row mb-2">
        <div class="col-md-3">
          <strong>Status</strong>
        </div>
        <div class="col-md-9">{{ statusLabel }}</div>
      </div>
      <div class="row mb-2">
        <div class="col-md-3">
          <strong>Account</strong>
        </div>
        <div class="col-md-9">{{ accountLabel }}</div>
      </div>
      <div v-if="status === 'ready'" class="row mb-2">
        <div class="col-md-3">
          <strong>Payments</strong>
        </div>
        <div class="col-md-9">Enabled for your account</div>
      </div>

      <div class="mt-3">
        <CButton
          color="primary"
          :disabled="ctaLoading"
          data-test="stripe-primary-cta"
          @click="onPrimaryClick"
        >
          {{ primaryCtaLabel }}
        </CButton>

        <CButton
          v-if="status === 'ready'"
          variant="outline"
          color="secondary"
          :disabled="loading"
          class="ml-2"
          data-test="stripe-refresh-readiness"
          @click="refreshReadiness"
        >
          Refresh readiness
        </CButton>
      </div>

      <CAlert
        v-if="status === 'ready' && expressDashboardUnavailable"
        color="warning"
        class="mt-3"
        data-test="stripe-dashboard-fallback"
      >
        Stripe management temporarily unavailable — contact support.
      </CAlert>

      <CAlert
        v-if="errorMessage"
        color="danger"
        class="mt-3"
        data-test="stripe-error-message"
      >
        {{ errorMessage }}
      </CAlert>
    </div>
  </div>
</template>

<script>
import { http } from '@/config/httpCommon'

const STATUS_NOT_CONNECTED = 'not_connected'
const STATUS_SETUP_PENDING = 'setup_pending'
const STATUS_READY = 'ready'

// Returned by POST stripe-connect/connect (422) when Stripe will not open
// a connected account for the company's country. Retrying never helps, so
// this must not fall through to the generic "please retry" copy.
const ERROR_COUNTRY_NOT_SUPPORTED = 'country_not_supported'

const STATUS_COPY = {
  [STATUS_NOT_CONNECTED]: {
    statusLabel: 'Not connected',
    accountLabel: 'No Stripe account connected',
    helperText:
      'Accept online payments through Stripe-hosted Checkout. Connect your Stripe account to start accepting payments.',
    primaryCtaLabel: 'Connect Stripe',
  },
  [STATUS_SETUP_PENDING]: {
    statusLabel: 'Setup pending',
    accountLabel: 'Stripe account connected, setup incomplete',
    helperText:
      'Your Stripe setup is not complete yet. Complete setup to start accepting online payments.',
    primaryCtaLabel: 'Continue Stripe setup',
  },
  [STATUS_READY]: {
    statusLabel: 'Ready',
    accountLabel: 'Stripe account connected',
    helperText:
      'Stripe is connected and ready. Customers can pay using Stripe-hosted Checkout.',
    primaryCtaLabel: 'Manage Stripe connection',
  },
}

export default {
  name: 'StripeConnectSection',
  data() {
    return {
      status: STATUS_NOT_CONNECTED,
      loading: false,
      ctaLoading: false,
      expressDashboardUnavailable: false,
      errorMessage: '',
    }
  },
  computed: {
    statusLabel() {
      return STATUS_COPY[this.status].statusLabel
    },
    accountLabel() {
      return STATUS_COPY[this.status].accountLabel
    },
    helperText() {
      return STATUS_COPY[this.status].helperText
    },
    primaryCtaLabel() {
      return STATUS_COPY[this.status].primaryCtaLabel
    },
  },
  mounted() {
    // When the merchant returns from Stripe-hosted onboarding the URL
    // arrives with ?refresh=1 — auto-trigger the server-side readiness
    // re-check (story open question 3). Otherwise just fetch current state.
    if (this.$route && this.$route.query && this.$route.query.refresh) {
      this.refreshReadiness()
    } else {
      this.fetchStatus()
    }
  },
  methods: {
    async fetchStatus() {
      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await http.get('stripe-connect/status')
        this.status = data && data.status ? data.status : STATUS_NOT_CONNECTED
      } catch (e) {
        this.errorMessage = 'Could not load Stripe status. Please retry.'
      } finally {
        this.loading = false
      }
    },
    async refreshReadiness() {
      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await http.post('stripe-connect/refresh-readiness')
        this.status = data && data.status ? data.status : STATUS_NOT_CONNECTED
      } catch (e) {
        this.errorMessage = 'Could not refresh readiness. Please retry.'
      } finally {
        this.loading = false
      }
    },
    onPrimaryClick() {
      if (this.status === STATUS_NOT_CONNECTED) {
        return this.connectStripe()
      }
      if (this.status === STATUS_SETUP_PENDING) {
        return this.continueSetup()
      }
      if (this.status === STATUS_READY) {
        return this.manageStripe()
      }
    },
    async connectStripe() {
      this.ctaLoading = true
      this.errorMessage = ''
      try {
        const { data } = await http.post('stripe-connect/connect')
        if (data && data.redirect_url) {
          window.location.href = data.redirect_url
          return
        }
        this.errorMessage = 'Stripe onboarding is unavailable right now. Please retry.'
      } catch (e) {
        this.errorMessage = this.connectErrorMessage(e)
      } finally {
        this.ctaLoading = false
      }
    },
    connectErrorMessage(e) {
      const payload = (e && e.response && e.response.data) || {}

      if (payload.status === ERROR_COUNTRY_NOT_SUPPORTED) {
        const country = payload.country
        return country
          ? `Stripe cannot open a payments account for your country (${country}). Please contact support so we can enable payments for you.`
          : 'Stripe cannot open a payments account for your country. Please contact support so we can enable payments for you.'
      }

      return 'Could not start Stripe onboarding. Please retry.'
    },
    async continueSetup() {
      this.ctaLoading = true
      this.errorMessage = ''
      try {
        const { data } = await http.post('stripe-connect/account-link')
        if (data && data.redirect_url) {
          window.location.href = data.redirect_url
          return
        }
        this.errorMessage = 'Stripe onboarding is unavailable right now. Please retry.'
      } catch (e) {
        this.errorMessage = 'Could not resume Stripe setup. Please retry.'
      } finally {
        this.ctaLoading = false
      }
    },
    async manageStripe() {
      this.ctaLoading = true
      this.expressDashboardUnavailable = false
      this.errorMessage = ''
      try {
        const { data } = await http.post('stripe-connect/dashboard-link')
        if (data && data.url) {
          window.open(data.url, '_blank', 'noopener')
          return
        }
        this.expressDashboardUnavailable = true
      } catch (e) {
        this.expressDashboardUnavailable = true
      } finally {
        this.ctaLoading = false
      }
    },
  },
}
</script>
